'use client';

import { useTranslations } from 'next-intl';
import { cn } from '@/shared/utils/cn';
import { MIC_SILENT_LEVEL } from '../audio/levelMeter';

interface MicLevelMeterProps {
  /** Live level 0..1 (from useAudioRecorder / the realtime session). */
  level: number;
  /** Paused recording — bars off, no "no signal" hint. */
  paused?: boolean;
  /** Seconds recorded so far — the "no signal" hint waits 2 s to avoid flashing. */
  elapsedSeconds?: number;
  /** 'full' = 16 bars + hint (dialogs); 'compact' = 6 short bars inline (buttons). */
  variant?: 'full' | 'compact';
  className?: string;
}

/**
 * The ONE microphone level indicator. A ticking timer never showed whether
 * the mic was heard; this does — and says so when it is not.
 */
export function MicLevelMeter({ level, paused, elapsedSeconds = 0, variant = 'full', className }: MicLevelMeterProps) {
  const t = useTranslations('cloneVoice');
  const bars = variant === 'compact' ? 6 : 16;
  const silent = !paused && elapsedSeconds >= 2 && level < MIC_SILENT_LEVEL;
  return (
    <div
      className={cn('relative flex items-center gap-2', variant === 'compact' ? 'w-10' : 'w-full', className)}
      data-testid="mic-level-meter"
      aria-label={`${Math.round(level * 100)}%`}
    >
      {/* Fixed-height bar row: the meter must never change the layout around it. */}
      <div className={cn('flex flex-1 items-end gap-0.5', variant === 'compact' ? 'h-3' : 'h-4')}>
        {Array.from({ length: bars }, (_, i) => {
          const on = !paused && level * bars > i;
          const hot = i / bars >= 0.8;
          const warm = i / bars >= 0.62;
          return (
            <div
              key={i}
              className={cn(
                'flex-1 rounded-sm transition-colors duration-75',
                on ? (hot ? 'bg-destructive' : warm ? 'bg-warning' : 'bg-success') : 'bg-muted',
              )}
              style={{ height: `${40 + (i / bars) * 60}%` }}
            />
          );
        })}
      </div>
      {variant === 'full' && silent && (
        // Overlaid, not inline: an inline label widened the row and shoved the
        // neighbours around every time it appeared.
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center rounded bg-background/80 text-xs text-warning-foreground dark:text-warning">
          {t('noSignal')}
        </span>
      )}
    </div>
  );
}
